import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { GL_VP, US_STATES } from '../../resources/constants';
import { useChartAccountApi } from '../../resources/hooks/api/chartAccountApiHook';
import { getBranch } from '../../store/user/selectors';
import '../../assets/css/ios-switch.css';

/**
 * Record / edit one AP disbursement.
 *
 * Three rules are mirrored from gl-api rather than left to the round trip,
 * because each of them silently misstates a return when wrong:
 *
 *   - the box must belong to the form type (NEC-1 only on 1099-NEC),
 *   - withholding cannot exceed the gross,
 *   - a state is required once state tax is withheld.
 *
 * Net is shown but never entered: it is gross minus withholding, and the API
 * derives it. Box totals aggregate gross, never net.
 */

export interface PaymentFormValues {
  id?: number;
  vendor_id: string;
  payment_date: string;
  gross_amount: string;
  federal_tax_withheld: string;
  state_tax_withheld: string;
  payment_method: string;
  is_1099_reportable: string;
  reportable_form_type: string;
  reportable_box: string;
  reference_number: string;
  withholding_state_code: string;
  memo: string;
  post_to_register?: boolean;
  expense_master_chart_of_account_id?: string;
  funding_master_chart_of_account_id?: string;
}

export const EMPTY_PAYMENT: PaymentFormValues = {
  vendor_id: '',
  payment_date: '',
  gross_amount: '',
  federal_tax_withheld: '',
  state_tax_withheld: '',
  payment_method: '1',
  is_1099_reportable: '',
  reportable_form_type: '',
  reportable_box: '',
  reference_number: '',
  withholding_state_code: '',
  memo: '',
  post_to_register: true,
  expense_master_chart_of_account_id: '',
  funding_master_chart_of_account_id: '',
};

/**
 * Values match src/apps/tax_1099/constants.py PaymentMethod. 1-9 are reportable;
 * 10+ are excluded under IRC §6050W because the settlement entity reports them
 * on Form 1099-K.
 */
export const PAYMENT_METHODS = [
  { value: '1', key: 'method.check', reportable: true },
  { value: '2', key: 'method.ach', reportable: true },
  { value: '3', key: 'method.wire', reportable: true },
  { value: '4', key: 'method.cash', reportable: true },
  { value: '5', key: 'method.direct_deposit', reportable: true },
  { value: '10', key: 'method.credit_card', reportable: false },
  { value: '11', key: 'method.debit_card', reportable: false },
  { value: '12', key: 'method.third_party_network', reportable: false },
  { value: '99', key: 'method.other_non_reportable', reportable: false },
];

const REPORTABLE_METHOD_VALUES = new Set(
  PAYMENT_METHODS.filter((method) => method.reportable).map((method) => method.value),
);

export const isReportableMethod = (value: string) =>
  REPORTABLE_METHOD_VALUES.has(value);

const STATE_OPTIONS = US_STATES.map((state) => ({
  value: state.code,
  label: `${state.code} — ${state.name}`,
}));

// Mirrors BOX_CODES_BY_FORM_TYPE. Withholding and state boxes are absent on
// purpose -- listing them would let the same money be counted twice.
const BOXES_BY_FORM_TYPE: Record<string, string[]> = {
  '1099-NEC': ['NEC-1'],
  '1099-MISC': ['MISC-1', 'MISC-2', 'MISC-3', 'MISC-6', 'MISC-10'],
};

const FORM_TYPES = Object.keys(BOXES_BY_FORM_TYPE);

const toNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

interface VendorOption {
  id: number;
  vendor_code: string;
  display_name: string;
  default_form_type?: string | null;
  default_box?: string | null;
}

interface Props {
  show: boolean;
  saving: boolean;
  initial: PaymentFormValues;
  vendors: VendorOption[];
  onClose: () => void;
  onSubmit: (values: PaymentFormValues) => void;
  onOpenQuickAddVendor?: (searchQuery: string, draft: PaymentFormValues) => void;
}

const PaymentFormModal: React.FC<Props> = ({
  show, saving, initial, vendors, onClose, onSubmit, onOpenQuickAddVendor,
}) => {
  const { t } = useTranslation('Tax1099Payments');
  const [values, setValues] = useState<PaymentFormValues>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEdit = Boolean(initial.id);

  const branch = useSelector(getBranch);
  const { glAccountsList } = useChartAccountApi();
  const [expenseAccounts, setExpenseAccounts] = useState<Array<{ id: number; label: string }>>([]);
  const [bankAccounts, setBankAccounts] = useState<Array<{ id: number; label: string }>>([]);

  useEffect(() => {
    if (isEdit || !show || !branch?.id) return;
    const toOptions = (resp: any) => {
      const rows = resp?.data?.data?.result ?? [];
      return rows.map((row: any) => ({ id: row.id, label: `${row.account} ${row.name}` }));
    };
    // Same account-section filtering ViewRegister/adjustments use: expense
    // and bank/cash are different COA sections, not one shared list.
    glAccountsList(
      { branch_id: branch.id, parent_account_in: GL_VP.EXPENSE.account.join(',') },
      (_msg: string, resp: any) => setExpenseAccounts(toOptions(resp)),
      () => setExpenseAccounts([])
    );
    glAccountsList(
      { branch_id: branch.id, parent_account_in: GL_VP.BANK_ACCOUNT.account.join(',') },
      (_msg: string, resp: any) => setBankAccounts(toOptions(resp)),
      () => setBankAccounts([])
    );
    // eslint-disable-next-line
  }, [isEdit, show, branch?.id]);

  const selectedVendor = useMemo(
    () => vendors.find((candidate) => String(candidate.id) === String(values.vendor_id)),
    [vendors, values.vendor_id],
  );

  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const vendorDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedVendor) {
      setVendorSearchQuery(`${selectedVendor.vendor_code} — ${selectedVendor.display_name}`);
    } else {
      setVendorSearchQuery('');
    }
  }, [selectedVendor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        vendorDropdownRef.current &&
        !vendorDropdownRef.current.contains(event.target as Node)
      ) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredVendors = useMemo(() => {
    if (!vendorSearchQuery.trim()) {
      return vendors;
    }
    const q = vendorSearchQuery.toLowerCase().trim();
    return vendors.filter(
      (v) =>
        v.display_name.toLowerCase().includes(q) ||
        v.vendor_code.toLowerCase().includes(q),
    );
  }, [vendors, vendorSearchQuery]);

  useEffect(() => {
    const next = { ...initial };
    if (next.reportable_form_type === '1099-NEC' && (!next.reportable_box || next.reportable_box === '')) {
      next.reportable_box = 'NEC-1';
    }
    setValues(next);
    setErrors({});
  }, [initial, show]);

  const setField = (field: keyof PaymentFormValues, value: any) => {
    setValues((previous) => {
      const next = { ...previous, [field]: value };
      if (field === 'reportable_form_type') {
        if (value === '1099-NEC') {
          next.reportable_box = 'NEC-1';
        } else if (!BOXES_BY_FORM_TYPE[value]?.includes(next.reportable_box)) {
          next.reportable_box = '';
        }
      }
      return next;
    });
    setErrors((previous) => {
      const next = { ...previous };
      delete next[field as string];
      return next;
    });
  };

  /** Changing the form type can orphan the selected box, so clear it, or auto-select if 1099-NEC. */
  const setFormType = (formType: string) => {
    const validBoxes = BOXES_BY_FORM_TYPE[formType] || [];
    let nextBox = validBoxes.includes(values.reportable_box)
      ? values.reportable_box
      : '';
    if (formType === '1099-NEC') {
      nextBox = 'NEC-1';
    }
    setValues((previous) => ({
      ...previous,
      reportable_form_type: formType,
      reportable_box: nextBox,
    }));
    setErrors({});
  };

  /** Picking a vendor inherits its box mapping, unless one is already chosen. */
  const setVendor = (vendorId: string) => {
    const vendor = vendors.find((candidate) => String(candidate.id) === vendorId);
    const formType = values.reportable_form_type || vendor?.default_form_type || '';
    let box = values.reportable_box || vendor?.default_box || '';
    if (formType === '1099-NEC' && (!box || box === '')) {
      box = 'NEC-1';
    }
    setValues((previous) => ({
      ...previous,
      vendor_id: vendorId,
      reportable_form_type: formType,
      reportable_box: box,
    }));
    setErrors({});
  };

  const boxOptions = BOXES_BY_FORM_TYPE[values.reportable_form_type] || [];

  const withheld = toNumber(values.federal_tax_withheld) + toNumber(values.state_tax_withheld);
  const net = useMemo(
    () => toNumber(values.gross_amount) - withheld,
    [values.gross_amount, withheld],
  );

  // Blank means "derive from the method"; show what the API will decide.
  const derivedReportable = isReportableMethod(values.payment_method);
  const effectiveReportable = values.is_1099_reportable === ''
    ? derivedReportable
    : values.is_1099_reportable === '1';

  const validate = (): boolean => {
    const found: Record<string, string> = {};

    if (!values.vendor_id) {
      found.vendor_id = t('form.errors.vendor_required');
    }
    if (!values.payment_date) {
      found.payment_date = t('form.errors.payment_date_required');
    }
    if (toNumber(values.gross_amount) <= 0) {
      found.gross_amount = t('form.errors.gross_amount_positive');
    }
    if (withheld > toNumber(values.gross_amount)) {
      found.federal_tax_withheld = t('form.errors.withholding_exceeds_gross');
    }
    if (toNumber(values.state_tax_withheld) > 0 && !values.withholding_state_code) {
      found.withholding_state_code = t('form.errors.state_code_required');
    }
    if (!isEdit && values.post_to_register) {
      if (!values.expense_master_chart_of_account_id) {
        found.expense_master_chart_of_account_id = t('form.errors.expense_account_required');
      }
      if (!values.funding_master_chart_of_account_id) {
        found.funding_master_chart_of_account_id = t('form.errors.funding_account_required');
      }
    }
    if (values.reportable_box && !values.reportable_form_type) {
      found.reportable_form_type = t('form.errors.form_type_required_for_box');
    }
    if (
      values.reportable_form_type
      && values.reportable_box
      && !boxOptions.includes(values.reportable_box)
    ) {
      found.reportable_box = t('form.errors.box_not_valid_for_form_type');
    }

    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (validate()) {
      onSubmit(values);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static" centered>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? t('form.title_edit') : t('form.title_create')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            <Form.Group className="col-md-6 mb-3" ref={vendorDropdownRef}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="mb-0">
                  {t('form.vendor')} <span className="text-danger">*</span>
                </Form.Label>
                {!isEdit && onOpenQuickAddVendor && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none small fw-semibold"
                    onClick={() => {
                      setShowVendorDropdown(false);
                      onOpenQuickAddVendor(
                        selectedVendor ? '' : vendorSearchQuery.trim(),
                        values,
                      );
                    }}
                  >
                    {t('form.quick_add_vendor', '+ New Vendor')}
                  </Button>
                )}
              </div>

              {isEdit ? (
                <>
                  <Form.Control
                    type="text"
                    value={
                      selectedVendor
                        ? `${selectedVendor.vendor_code} — ${selectedVendor.display_name}`
                        : values.vendor_id
                    }
                    disabled
                  />
                  <Form.Text muted>{t('form.vendor_locked')}</Form.Text>
                </>
              ) : (
                <div className="position-relative">
                  <Form.Control
                    type="search"
                    placeholder={t('form.vendor_search_placeholder', 'Type vendor name or code...')}
                    value={vendorSearchQuery}
                    isInvalid={Boolean(errors.vendor_id)}
                    onChange={(event) => {
                      const val = event.target.value;
                      setVendorSearchQuery(val);
                      setShowVendorDropdown(true);
                      if (!val) {
                        setVendor('');
                      }
                    }}
                    onFocus={() => setShowVendorDropdown(true)}
                  />
                  {values.vendor_id && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                      style={{ padding: '0 10px', fontSize: '14px', zIndex: 5 }}
                      onClick={() => {
                        setVendor('');
                        setVendorSearchQuery('');
                        setShowVendorDropdown(false);
                      }}
                      title={t('form.clear_vendor', 'Clear vendor')}
                    >
                      ✕
                    </button>
                  )}
                  {showVendorDropdown && (
                    <div
                      className="dropdown-menu show w-100 shadow border"
                      style={{
                        maxHeight: '220px',
                        overflowY: 'auto',
                        zIndex: 1060,
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                      }}
                    >
                      {filteredVendors.length === 0 ? (
                        <div className="p-2">
                          <div className="small text-muted mb-1 px-2">
                            {t('form.no_matching_vendors', {
                              query: vendorSearchQuery,
                              defaultValue: `No vendors matching "${vendorSearchQuery}"`,
                            })}
                          </div>
                          {onOpenQuickAddVendor && vendorSearchQuery.trim() && (
                            <button
                              type="button"
                              className="dropdown-item small text-primary fw-bold rounded"
                              onClick={() => {
                                setShowVendorDropdown(false);
                                // Carries the in-progress payment too, so the
                                // draft survives the quick-add detour.
                                onOpenQuickAddVendor(vendorSearchQuery.trim(), values);
                              }}
                            >
                              {t('form.create_vendor_prompt', {
                                name: vendorSearchQuery.trim(),
                                defaultValue: `+ Create "${vendorSearchQuery.trim()}" on the fly`,
                              })}
                            </button>
                          )}
                        </div>
                      ) : (
                        filteredVendors.map((vendor) => (
                          <button
                            key={vendor.id}
                            type="button"
                            className={`dropdown-item small ${
                              String(vendor.id) === values.vendor_id ? 'active fw-bold' : ''
                            }`}
                            onClick={() => {
                              setVendor(String(vendor.id));
                              setShowVendorDropdown(false);
                            }}
                          >
                            <span className="fw-bold">{vendor.vendor_code}</span> — {vendor.display_name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.vendor_id}
                  </Form.Control.Feedback>
                </div>
              )}
            </Form.Group>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>
                {t('form.payment_date')} <span className="text-danger">*</span>
              </Form.Label>
              {/* Native date input: no picker dependency, and it hands back the
                  ISO string the API wants. */}
              <Form.Control
                type="date"
                value={values.payment_date}
                isInvalid={Boolean(errors.payment_date)}
                onChange={(event) => setField('payment_date', event.target.value)}
              />
              <Form.Control.Feedback type="invalid">{errors.payment_date}</Form.Control.Feedback>
              <Form.Text muted>{t('form.payment_date_help')}</Form.Text>
            </Form.Group>

            <div className="col-12"><hr /></div>

            <Form.Group className="col-md-3 mb-3">
              <Form.Label>
                {t('form.gross_amount')} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={values.gross_amount}
                isInvalid={Boolean(errors.gross_amount)}
                onChange={(event) => setField('gross_amount', event.target.value)}
              />
              <Form.Control.Feedback type="invalid">{errors.gross_amount}</Form.Control.Feedback>
              <Form.Text muted>{t('form.gross_amount_help')}</Form.Text>
            </Form.Group>

            <Form.Group className="col-md-3 mb-3">
              <Form.Label>{t('form.federal_tax_withheld')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={values.federal_tax_withheld}
                isInvalid={Boolean(errors.federal_tax_withheld)}
                onChange={(event) => setField('federal_tax_withheld', event.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {errors.federal_tax_withheld}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="col-md-3 mb-3">
              <Form.Label>{t('form.state_tax_withheld')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={values.state_tax_withheld}
                onChange={(event) => setField('state_tax_withheld', event.target.value)}
              />
            </Form.Group>

            <Form.Group className="col-md-3 mb-3">
              <Form.Label>{t('form.withholding_state_code')}</Form.Label>
              <Select
                classNamePrefix="react-select"
                placeholder={t('form.select_state', 'Select state')}
                isClearable
                options={STATE_OPTIONS}
                value={
                  STATE_OPTIONS.find((option) => option.value === values.withholding_state_code) ||
                  (values.withholding_state_code
                    ? { value: values.withholding_state_code, label: values.withholding_state_code }
                    : null)
                }
                onChange={(selected) =>
                  setField('withholding_state_code', selected ? selected.value : '')
                }
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: errors.withholding_state_code ? '#dc3545' : base.borderColor,
                    boxShadow: state.isFocused && errors.withholding_state_code ? '0 0 0 0.25rem rgba(220,53,69,.25)' : base.boxShadow,
                  }),
                }}
              />
              {errors.withholding_state_code && (
                <div className="invalid-feedback d-block">{errors.withholding_state_code}</div>
              )}
            </Form.Group>

            <div className="col-12 mb-3">
              {/* Read-only: the API derives it, so an editable field here could
                  disagree with what is stored. */}
              <div className="alert alert-light border mb-0 py-2">
                {t('form.net_preview', { amount: net.toFixed(2) })}
              </div>
            </div>

            <div className="col-12"><hr /></div>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>
                {t('form.payment_method')} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={values.payment_method}
                onChange={(event) => setField('payment_method', event.target.value)}
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {t(method.key)}
                  </option>
                ))}
              </Form.Select>
              {!derivedReportable && (
                <Form.Text className="text-warning">{t('form.excluded_6050w')}</Form.Text>
              )}
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.reportable')}</Form.Label>
              <Form.Select
                value={values.is_1099_reportable}
                onChange={(event) => setField('is_1099_reportable', event.target.value)}
              >
                <option value="">
                  {t('form.reportable_derive', {
                    value: derivedReportable ? t('form.yes') : t('form.no'),
                  })}
                </option>
                <option value="1">{t('form.reportable_force_yes')}</option>
                <option value="0">{t('form.reportable_force_no')}</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.reference_number')}</Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={values.reference_number}
                onChange={(event) => setField('reference_number', event.target.value)}
              />
              <Form.Text muted>{t('form.reference_number_help')}</Form.Text>
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.reportable_form_type')}</Form.Label>
              <Form.Select
                value={values.reportable_form_type}
                isInvalid={Boolean(errors.reportable_form_type)}
                onChange={(event) => setFormType(event.target.value)}
              >
                <option value="">{t('form.not_set')}</option>
                {FORM_TYPES.map((formType) => (
                  <option key={formType} value={formType}>{formType}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.reportable_form_type}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.reportable_box')}</Form.Label>
              <Form.Select
                value={values.reportable_box}
                disabled={!values.reportable_form_type}
                isInvalid={Boolean(errors.reportable_box)}
                onChange={(event) => setField('reportable_box', event.target.value)}
              >
                {values.reportable_form_type !== '1099-NEC' && (
                  <option value="">{t('form.not_set')}</option>
                )}
                {boxOptions.map((box) => (
                  <option key={box} value={box}>{box}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.reportable_box}
              </Form.Control.Feedback>
              {!values.reportable_form_type && (
                <Form.Text muted>{t('form.box_needs_form_type')}</Form.Text>
              )}
            </Form.Group>

            <Form.Group className="col-md-4 mb-3">
              <Form.Label>{t('form.effective_reportable')}</Form.Label>
              <div className="pt-2">
                <span className={effectiveReportable ? 'badge bg-success' : 'badge bg-secondary'}>
                  {effectiveReportable ? t('form.yes') : t('form.no')}
                </span>
              </div>
            </Form.Group>

            <Form.Group className="col-12 mb-3">
              <Form.Label>{t('form.memo')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={values.memo}
                onChange={(event) => setField('memo', event.target.value)}
              />
            </Form.Group>

            {!isEdit && (
              <div className="col-12 mb-3">
                <div className="ios-switch-field p-2 bg-light rounded border px-3">
                  <input
                    type="checkbox"
                    className="ios-switch"
                    id="post_to_register"
                    aria-describedby="post_to_register_help"
                    checked={Boolean(values.post_to_register)}
                    onChange={(event) => setField('post_to_register', event.target.checked)}
                  />
                  <div>
                    <label className="fw-semibold" htmlFor="post_to_register">
                      {t('form.post_to_register', 'Also post to Transaction Register (GL)')}
                    </label>
                    <div className="small text-muted" id="post_to_register_help">
                      {t('form.post_to_register_help', 'Books a disbursement line in the bank register with this vendor')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isEdit && values.post_to_register && (
              <>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>
                    {t('form.expense_account', 'Expense Account')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    placeholder={t('form.select_account', 'Select account')}
                    isClearable
                    options={expenseAccounts.map((account) => ({ value: String(account.id), label: account.label }))}
                    value={
                      expenseAccounts
                        .map((account) => ({ value: String(account.id), label: account.label }))
                        .find((option) => option.value === values.expense_master_chart_of_account_id) || null
                    }
                    onChange={(selected) =>
                      setField('expense_master_chart_of_account_id', selected ? selected.value : '')
                    }
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderColor: errors.expense_master_chart_of_account_id ? '#dc3545' : base.borderColor,
                        boxShadow: state.isFocused && errors.expense_master_chart_of_account_id ? '0 0 0 0.25rem rgba(220,53,69,.25)' : base.boxShadow,
                      }),
                    }}
                  />
                  {errors.expense_master_chart_of_account_id && (
                    <div className="invalid-feedback d-block">{errors.expense_master_chart_of_account_id}</div>
                  )}
                </Form.Group>

                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>
                    {t('form.funding_account', 'Paid From (Bank/Cash Account)')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    placeholder={t('form.select_account', 'Select account')}
                    isClearable
                    options={bankAccounts.map((account) => ({ value: String(account.id), label: account.label }))}
                    value={
                      bankAccounts
                        .map((account) => ({ value: String(account.id), label: account.label }))
                        .find((option) => option.value === values.funding_master_chart_of_account_id) || null
                    }
                    onChange={(selected) =>
                      setField('funding_master_chart_of_account_id', selected ? selected.value : '')
                    }
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderColor: errors.funding_master_chart_of_account_id ? '#dc3545' : base.borderColor,
                        boxShadow: state.isFocused && errors.funding_master_chart_of_account_id ? '0 0 0 0.25rem rgba(220,53,69,.25)' : base.boxShadow,
                      }),
                    }}
                  />
                  {errors.funding_master_chart_of_account_id && (
                    <div className="invalid-feedback d-block">{errors.funding_master_chart_of_account_id}</div>
                  )}
                </Form.Group>
              </>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('form.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? t('form.saving') : t('form.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PaymentFormModal;
